import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Book, Search, Filter, Plus, Loader, BookOpen, X, User } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Courses Page component
 * Lists all courses with filtering options
 */
const CoursesPage = () => {
  const { user } = useContext(AuthContext);
  const [courses, setCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState(null);
  
  // Course request state
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestingNewDepartment, setRequestingNewDepartment] = useState(false);
  const [requestingNewProfessor, setRequestingNewProfessor] = useState(false);
  const [includeSection, setIncludeSection] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [courseRequest, setCourseRequest] = useState({
    departmentId: '',
    newDepartmentCode: '',
    newDepartmentName: '',
    courseNumber: '',
    courseName: '',
    courseDescription: '',
    sectionTerm: '',
    sectionYear: new Date().getFullYear(),
    professorId: '',
    newProfessorName: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Determine the school ID from different possible structures
        let schoolId;
        if (user?.schoolId) {
          console.log("Using user.schoolId:", user.schoolId);
          schoolId = user.schoolId;
        } else if (user?.school?.id) {
          console.log("Using user.school.id:", user.school.id);
          schoolId = user.school.id;
        } else {
          console.log("Could not find school ID in user object:", user);
          throw new Error('No school ID available');
        }
        
        console.log("Fetching courses for school ID:", schoolId);
        
        // Fetch courses and departments in parallel
        const [coursesRes, departmentsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/courses`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/departments`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
        ]);
  
        if (!coursesRes.ok) {
          console.error("Courses API response not OK:", coursesRes.status, coursesRes.statusText);
          throw new Error('Failed to fetch courses');
        }
  
        if (!departmentsRes.ok) {
          console.error("Departments API response not OK:", departmentsRes.status, departmentsRes.statusText);
          throw new Error('Failed to fetch departments');
        }
  
        const coursesData = await coursesRes.json();
        const departmentsData = await departmentsRes.json();
        
        console.log("Fetched courses:", coursesData.length, "courses");
        console.log("Fetched departments:", departmentsData.length, "departments");
        
        setCourses(coursesData);
        setDepartments(departmentsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message || 'An error occurred while loading courses');
        toast.error('Failed to load courses');
      } finally {
        setLoading(false);
      }
    };
  
    if (user) {
      console.log("User is available, attempting to fetch data");
      fetchData();
    } else {
      console.log("User is not available yet");
      setLoading(false);
    }
  }, [user]);

  // Fetch professors when needed for section request
  useEffect(() => {
    const fetchProfessors = async () => {
      if (!user || !includeSection) return;
      
      let schoolId = user.schoolId || user.school?.id;
      if (!schoolId) return;
      
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/professors`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (res.ok) {
          const data = await res.json();
          setProfessors(data);
        } else {
          console.error('Failed to fetch professors:', res.status);
        }
      } catch (error) {
        console.error('Error fetching professors:', error);
      }
    };
    
    fetchProfessors();
  }, [user, includeSection]);

  // Filter courses based on search query and department filter
  const filteredCourses = courses.filter(course => {
    const matchesSearch = 
      course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (course.description && course.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesDepartment = selectedDepartment ? course.departmentId === selectedDepartment : true;
    
    return matchesSearch && matchesDepartment;
  });

  // Handle course request submission
  const handleCourseRequestSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setRequestLoading(true);
      
      // Validate form data
      if (requestingNewDepartment && (!courseRequest.newDepartmentCode || !courseRequest.newDepartmentName)) {
        toast.error('Please provide both department code and name');
        return;
      }
      
      if (includeSection && !courseRequest.sectionTerm) {
        toast.error('Please select a section term');
        return;
      }
      
      if (includeSection && requestingNewProfessor && !courseRequest.newProfessorName) {
        toast.error('Please provide professor name');
        return;
      }
      
      // Get school ID
      const schoolId = user.schoolId || user.school?.id;
      
      // Construct request data
      const requestData = {
        schoolId,
        departmentId: requestingNewDepartment ? null : courseRequest.departmentId,
        code: courseRequest.courseNumber,
        name: courseRequest.courseName,
        description: courseRequest.courseDescription,
        newDepartmentCode: requestingNewDepartment ? courseRequest.newDepartmentCode : null,
        newDepartmentName: requestingNewDepartment ? courseRequest.newDepartmentName : null,
      };
      
      // Add section details if requesting
      if (includeSection) {
        requestData.includeSection = true;
        requestData.section = {
          semester: courseRequest.sectionTerm,
          year: courseRequest.sectionYear,
          professorId: requestingNewProfessor ? null : courseRequest.professorId,
          newProfessorName: requestingNewProfessor ? courseRequest.newProfessorName : null
        };
      }
      
      // Send request to API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/course-requests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit course request');
      }
      
      // Close modal and show success message
      setRequestModalOpen(false);
      toast.success('Course request submitted successfully! It will be reviewed by moderators.');
      
      // Reset form
      setCourseRequest({
        departmentId: '',
        newDepartmentCode: '',
        newDepartmentName: '',
        courseNumber: '',
        courseName: '',
        courseDescription: '',
        sectionTerm: '',
        sectionYear: new Date().getFullYear(),
        professorId: '',
        newProfessorName: ''
      });
      setIncludeSection(false);
      setRequestingNewDepartment(false);
      setRequestingNewProfessor(false);
    } catch (error) {
      console.error('Error submitting course request:', error);
      toast.error(error.message || 'Failed to submit course request. Please try again.');
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Courses
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Browse all courses at {user?.school?.name || 'your school'}
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
          {/* Student Action - Request Course */}
          {user && user.role === 'student' && (
            <button
              onClick={() => setRequestModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="mr-2 -ml-1 h-5 w-5" />
              Request Course
            </button>
          )}
          
          {/* Admin/Moderator Action - Add Course */}
          {user && (user.role === 'admin' || user.role === 'moderator') && (
            <Link
              to="/courses/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <Plus className="mr-2 -ml-1 h-5 w-5" />
              Add Course
            </Link>
          )}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-8">
        {/* Search bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search courses by name, code, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
        
        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
        >
          <Filter className="h-4 w-4 mr-1" />
          <span className="text-sm">{showFilters ? 'Hide filters' : 'Show filters'}</span>
        </button>
        
        {/* Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div>
              <label htmlFor="department-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Department
              </label>
              <select
                id="department-filter"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">All Departments</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.code} - {department.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Courses List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader className="h-8 w-8 text-red-500 animate-spin" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading courses...</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="text-center py-12">
          {searchQuery || selectedDepartment ? (
            <div>
              <BookOpen className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No courses found
              </h3>
              <p className="mt-1 text-gray-500 dark:text-gray-400">
                No courses match your current filters.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedDepartment('');
                }}
                className="mt-4 text-red-600 dark:text-red-400 hover:underline"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div>
              <Book className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No courses yet
              </h3>
              <p className="mt-1 text-gray-500 dark:text-gray-400">
                There are no courses available at your school yet.
              </p>
              {user && user.role === 'student' && (
                <button
                  onClick={() => setRequestModalOpen(true)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Request a Course
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course) => (
            <Link
              key={course.id}
              to={`/courses/${course.id}`}
              className="block bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow p-6 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex flex-col h-full">
                <div className="flex items-start">
                  <div className="bg-red-100 dark:bg-red-900 rounded-full p-3 text-red-600 dark:text-red-400">
                    <Book className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {course.code}: {course.name}
                    </h3>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                      {course.departmentName} ({course.departmentCode})
                    </p>
                  </div>
                </div>
                
                {course.description && (
                  <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                    {course.description}
                  </p>
                )}
                
                <div className="mt-auto pt-4">
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <span className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">
                      {course.sectionCount || 0} {course.sectionCount === 1 ? 'section' : 'sections'}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Course Request Modal */}
      {requestModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => !requestLoading && setRequestModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  onClick={() => !requestLoading && setRequestModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Request New Course</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Fill out this form to request a new course. Your request will be reviewed by moderators.
                    </p>
                  </div>
                  
                  <div className="mt-4">
                    <form onSubmit={handleCourseRequestSubmit} className="space-y-4">
                      {/* Department Selection */}
                      <div>
                        <label htmlFor="department" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Department
                        </label>
                        <select
                          id="department"
                          value={requestingNewDepartment ? 'new' : courseRequest.departmentId}
                          onChange={(e) => {
                            if (e.target.value === 'new') {
                              setRequestingNewDepartment(true);
                              setCourseRequest({
                                ...courseRequest,
                                departmentId: ''
                              });
                            } else {
                              setRequestingNewDepartment(false);
                              setCourseRequest({
                                ...courseRequest,
                                departmentId: e.target.value
                              });
                            }
                          }}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          required
                        >
                          <option value="">Select Department</option>
                          {departments.map((department) => (
                            <option key={department.id} value={department.id}>
                              {department.code} - {department.name}
                            </option>
                          ))}
                          <option value="new">Request New Department</option>
                        </select>
                      </div>
                      
                      {/* New Department Fields (if requesting) */}
                      {requestingNewDepartment && (
                        <>
                          <div>
                            <label htmlFor="new-department-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Department Code (e.g., POS)
                            </label>
                            <input
                              type="text"
                              id="new-department-code"
                              value={courseRequest.newDepartmentCode}
                              onChange={(e) => setCourseRequest({
                                ...courseRequest,
                                newDepartmentCode: e.target.value
                              })}
                              className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required={requestingNewDepartment}
                              maxLength={10}
                            />
                          </div>
                          <div>
                            <label htmlFor="new-department-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Department Name
                            </label>
                            <input
                              type="text"
                              id="new-department-name"
                              value={courseRequest.newDepartmentName}
                              onChange={(e) => setCourseRequest({
                                ...courseRequest,
                                newDepartmentName: e.target.value
                              })}
                              className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required={requestingNewDepartment}
                            />
                          </div>
                        </>
                      )}
                      
                      {/* Course Fields */}
                      <div>
                        <label htmlFor="course-number" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Course Number
                        </label>
                        <input
                          type="text"
                          id="course-number"
                          value={courseRequest.courseNumber}
                          onChange={(e) => setCourseRequest({
                            ...courseRequest,
                            courseNumber: e.target.value
                          })}
                          className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          required
                          maxLength={10}
                          placeholder="e.g., 101, 201, etc."
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="course-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Course Name
                        </label>
                        <input
                          type="text"
                          id="course-name"
                          value={courseRequest.courseName}
                          onChange={(e) => setCourseRequest({
                            ...courseRequest,
                            courseName: e.target.value
                          })}
                          className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          required
                          placeholder="e.g., Introduction to Computer Science"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="course-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Course Description
                        </label>
                        <textarea
                          id="course-description"
                          value={courseRequest.courseDescription}
                          onChange={(e) => setCourseRequest({
                            ...courseRequest,
                            courseDescription: e.target.value
                          })}
                          rows={3}
                          className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="Brief description of the course content and objectives"
                        />
                      </div>
                      
                      {/* Section Request Option */}
                      <div className="flex items-center">
                        <input
                          id="include-section"
                          type="checkbox"
                          checked={includeSection}
                          onChange={(e) => setIncludeSection(e.target.checked)}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <label htmlFor="include-section" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                          Also request first section for this course
                        </label>
                      </div>
                      
                      {/* Section Details (if requesting) */}
                      {includeSection && (
                        <>
                          <div>
                            <label htmlFor="section-term" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Section Term
                            </label>
                            <select
                              id="section-term"
                              value={courseRequest.sectionTerm}
                              onChange={(e) => setCourseRequest({
                                ...courseRequest,
                                sectionTerm: e.target.value
                              })}
                              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required={includeSection}
                            >
                              <option value="">Select Term</option>
                              <option value="Fall">Fall</option>
                              <option value="Winter">Winter</option>
                              <option value="Spring">Spring</option>
                              <option value="Summer">Summer</option>
                            </select>
                          </div>
                          
                          <div>
                            <label htmlFor="section-year" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Section Year
                            </label>
                            <input
                              type="number"
                              id="section-year"
                              value={courseRequest.sectionYear}
                              onChange={(e) => setCourseRequest({
                                ...courseRequest,
                                sectionYear: parseInt(e.target.value, 10)
                              })}
                              min={new Date().getFullYear()}
                              max={new Date().getFullYear() + 5}
                              className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required={includeSection}
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="professor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Professor
                            </label>
                            <select
                              id="professor"
                              value={requestingNewProfessor ? 'new' : courseRequest.professorId}
                              onChange={(e) => {
                                if (e.target.value === 'new') {
                                  setRequestingNewProfessor(true);
                                  setCourseRequest({
                                    ...courseRequest,
                                    professorId: ''
                                  });
                                } else {
                                  setRequestingNewProfessor(false);
                                  setCourseRequest({
                                    ...courseRequest,
                                    professorId: e.target.value
                                  });
                                }
                              }}
                              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required={includeSection}
                            >
                              <option value="">Select Professor</option>
                              {professors.map((professor) => (
                                <option key={professor.id} value={professor.id}>
                                  {professor.name}
                                </option>
                              ))}
                              <option value="new">Request New Professor</option>
                            </select>
                          </div>
                          
                          {/* New Professor Fields (if requesting) */}
                          {requestingNewProfessor && (
                            <div>
                              <label htmlFor="new-professor-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Professor Name
                              </label>
                              <input
                                type="text"
                                id="new-professor-name"
                                value={courseRequest.newProfessorName}
                                onChange={(e) => setCourseRequest({
                                  ...courseRequest,
                                  newProfessorName: e.target.value
                                })}
                                className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                required={requestingNewProfessor && includeSection}
                              />
                            </div>
                          )}
                        </>
                      )}
                      
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={requestLoading}
                          className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${
                            requestLoading ? 'opacity-70 cursor-not-allowed' : ''
                          }`}
                        >
                          {requestLoading ? (
                            <>
                              <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                              Submitting...
                            </>
                          ) : 'Submit Request'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setRequestModalOpen(false)}
                          disabled={requestLoading}
                          className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:w-auto sm:text-sm ${
                            requestLoading ? 'opacity-70 cursor-not-allowed' : ''
                          }`}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursesPage;